<template>
<nav class="vpn-nav" aria-label="Primary">
  <div class="vpn-nav__inner">
    <router-link class="vpn-nav__brand" to="/" title="MyVPNBook">
      <img class="vpn-nav__logo" src="@/assets/myvpnbook-logo.svg" alt="" width="40" height="40">
      <span class="vpn-nav__name">My<span class="vpn-nav__name-accent">VPN</span>Book</span>
    </router-link>

    <button type="button" class="vpn-nav__toggle" aria-label="Menu" @click="menuOpen = !menuOpen">
      <span />
      <span />
      <span />
    </button>

    <div class="vpn-nav__actions" :class="{ 'is-open': menuOpen }">
      <router-link class="vpn-nav__link" to="/" exact @click.native="menuOpen = false">Home</router-link>
      <details ref="deviceMenu" class="vpn-nav__dropdown">
        <summary class="vpn-nav__link vpn-nav__dropdown-toggle">
          Devices
          <svg class="vpn-nav__chevron" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          </svg>
        </summary>
        <div class="vpn-nav__menu">
          <router-link
            v-for="item in deviceLinks"
            :key="item.to"
            class="vpn-nav__menu-link"
            :to="item.to"
            @click.native="closeMenus"
          >
            {{ item.label }}
          </router-link>
        </div>
      </details>
      <router-link class="vpn-nav__link" to="/guides/" @click.native="menuOpen = false">Guides</router-link>
      <router-link class="vpn-nav__link" to="/common-vpn-problems/" @click.native="menuOpen = false">Common VPN Problems</router-link>
      <router-link class="vpn-nav__cta" to="/get-free-vpn/" @click.native="menuOpen = false">
        Get Free VPN
      </router-link>
      <details ref="langMenu" class="vpn-nav__dropdown vpn-nav__lang">
        <summary class="vpn-nav__link vpn-nav__lang-toggle" aria-label="Choose language">
          <svg class="vpn-nav__lang-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.6" />
            <path d="M4 12h16M12 4c2.2 2.5 3.3 5.2 3.3 8s-1.1 5.5-3.3 8c-2.2-2.5-3.3-5.2-3.3-8S9.8 6.5 12 4z" stroke="currentColor" stroke-width="1.4" />
          </svg>
          <span>{{ currentLanguageShort }}</span>
          <svg class="vpn-nav__chevron" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          </svg>
        </summary>
        <div class="vpn-nav__menu vpn-nav__lang-menu" role="listbox">
          <button
            v-for="lang in languages"
            :key="lang.code"
            type="button"
            class="vpn-nav__menu-link vpn-nav__lang-option"
            :class="{ 'is-active': lang.code === activeLanguageCode }"
            :aria-current="lang.code === activeLanguageCode ? 'true' : null"
            @click="onPickLanguage(lang)"
          >
            <span>{{ lang.label }}</span>
            <span v-if="lang.code === activeLanguageCode" class="vpn-nav__lang-check" aria-hidden="true">✓</span>
          </button>
        </div>
      </details>
    </div>
  </div>
</nav>
</template>

<script>
import { NAV_LANGUAGES, resolveNavLanguage } from '@/config/myvpnbookSite'

export default {
  name: 'HeadNav',
  props: {
    selectedLanguage: {
      type: String,
      default: 'en',
    },
    page: {
      type: String,
      default: 'home',
    },
  },
  data() {
    return {
      menuOpen: false,
      languages: NAV_LANGUAGES,
      deviceLinks: [
        { label: 'Android', to: '/vpn-for-android/' },
        { label: 'Windows', to: '/vpn-for-windows/' },
        { label: 'iPhone & iPad', to: '/vpn-for-iphone-ipad/' },
        { label: 'Mac', to: '/vpn-for-mac/' },
        { label: 'Chrome', to: '/vpn-for-chrome/' },
      ],
    }
  },
  computed: {
    activeLanguageCode() {
      const route = this.$route && this.$route.path
      if (route) return resolveNavLanguage(route)
      return this.selectedLanguage || 'en'
    },
    currentLanguageShort() {
      const current = this.languages.find((item) => item.code === this.activeLanguageCode)
      return (current && current.short) || 'EN'
    },
  },
  methods: {
    closeMenus() {
      this.menuOpen = false
      if (this.$refs.deviceMenu) {
        this.$refs.deviceMenu.open = false
      }
      if (this.$refs.langMenu) {
        this.$refs.langMenu.open = false
      }
    },
    onPickLanguage(lang) {
      if (!lang || !lang.path) {
        this.closeMenus()
        return
      }
      if (lang.code === this.activeLanguageCode) {
        this.closeMenus()
        return
      }
      this.closeMenus()
      this.menuOpen = false
      this.$router.push(lang.path).catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
.vpn-nav {
  --vpn-nav-font-size: 15px;
  --vpn-nav-font-family: 'Segoe UI', 'PingFang SC', 'Helvetica Neue', Arial, sans-serif;
  position: sticky;
  top: 0;
  z-index: 100;
  font-family: var(--vpn-nav-font-family);
  line-height: 1.5;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid var(--vpn-line);
}

.vpn-nav__inner {
  width: var(--vpn-content-width);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 64px;
}

.vpn-nav__brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: var(--vpn-ink);
  font-weight: 800;
  font-size: 18px;
}

.vpn-nav__name-accent {
  color: var(--vpn-brand);
}

.vpn-nav__actions {
  display: flex;
  align-items: center;
  gap: 4px 20px;
  flex-wrap: wrap;
  font-size: var(--vpn-nav-font-size);
}

.vpn-nav__link {
  color: var(--vpn-ink-muted);
  text-decoration: none;
  font-size: var(--vpn-nav-font-size);
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
  list-style: none;
}

.vpn-nav__link:hover,
.vpn-nav__link.router-link-active {
  color: var(--vpn-brand);
}

.vpn-nav__cta {
  display: inline-flex;
  align-items: center;
  padding: 10px 18px;
  border-radius: var(--vpn-radius-pill);
  background: var(--vpn-brand);
  color: #fff !important;
  font-weight: 700;
  font-size: var(--vpn-nav-font-size);
  text-decoration: none;
  box-shadow: var(--vpn-shadow-btn);
}

.vpn-nav__dropdown {
  position: relative;
}

.vpn-nav__dropdown-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.vpn-nav__dropdown-toggle::-webkit-details-marker {
  display: none;
}

.vpn-nav__chevron {
  width: 14px;
  height: 14px;
  transition: transform 0.2s ease;
}

.vpn-nav__dropdown[open] .vpn-nav__chevron {
  transform: rotate(180deg);
}

.vpn-nav__menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 200px;
  background: #fff;
  border: 1px solid var(--vpn-line);
  border-radius: 12px;
  padding: 8px;
  box-shadow: var(--vpn-shadow);
}

.vpn-nav__menu-link {
  display: block;
  padding: 10px 12px;
  border-radius: 8px;
  color: var(--vpn-ink);
  text-decoration: none;
  font-size: 14px;
}

.vpn-nav__menu-link:hover {
  background: var(--vpn-page-soft);
  color: var(--vpn-brand);
}

.vpn-nav__lang-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  list-style: none;
  white-space: nowrap;
}

.vpn-nav__lang-toggle::-webkit-details-marker {
  display: none;
}

.vpn-nav__lang-icon {
  flex: none;
  color: var(--vpn-brand);
}

.vpn-nav__lang-menu {
  right: 0;
  left: auto;
  min-width: 168px;
}

.vpn-nav__lang-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border: 0;
  background: none;
  font: inherit;
  padding-right: 16px;
  text-align: left;
  cursor: pointer;
}

.vpn-nav__lang-option.is-active {
  color: var(--vpn-brand);
  font-weight: 700;
}

.vpn-nav__lang-check {
  flex: none;
  margin-left: 12px;
  color: var(--vpn-brand);
  font-size: 13px;
}

.vpn-nav__toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
}

.vpn-nav__toggle span {
  display: block;
  width: 22px;
  height: 2px;
  background: var(--vpn-ink);
}

@media (max-width: 900px) {
  .vpn-nav__toggle {
    display: flex;
  }

  .vpn-nav__actions {
    display: none;
    position: absolute;
    top: 64px;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    background: #fff;
    border-bottom: 1px solid var(--vpn-line);
    padding: 16px;
    box-shadow: var(--vpn-shadow);
  }

  .vpn-nav__actions.is-open {
    display: flex;
  }

  .vpn-nav__dropdown .vpn-nav__menu {
    position: static;
    box-shadow: none;
    border: none;
    padding: 0 0 0 12px;
  }
}
</style>
